import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  IconButton,
  makeStyles,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { InfoCard } from '@backstage/core-components';
import { ASSIGNABLE_PROJECT_ROLES, RoleGate } from '../../roles';
import { AddMemberDialog } from './AddMemberDialog';
import { classifyMemberError, MemberErrorKind } from './memberErrors';
import { memberDisplayName } from './memberLabels';
import { RemoveMemberDialog } from './RemoveMemberDialog';
import { TransferOwnershipDialog } from './TransferOwnershipDialog';
import { MembersStatus, useProjectMembers } from './useProjectMembers';
import { ProjectMember, ProjectRole } from './types';

const useStyles = makeStyles(theme => ({
  subtle: {
    color: theme.palette.text.secondary,
  },
  caution: {
    color: theme.palette.error.main,
  },
  roleSelect: {
    minWidth: 160,
  },
  actionsCell: {
    width: 64,
  },
}));

type MembersState = ReturnType<typeof useProjectMembers>;

type ProjectMembersPanelProps = {
  projectRole: ProjectRole;
  members: MembersState['members'];
  status: MembersStatus;
  reload: MembersState['reload'];
  searchUsers: MembersState['searchUsers'];
  addMember: MembersState['addMember'];
  assignRole: MembersState['assignRole'];
  removeMember: MembersState['removeMember'];
  transferOwnership: MembersState['transferOwnership'];
};

function capabilityKey(role: ProjectRole) {
  if (role === 'Viewer') return 'capabilities.readOnly';
  if (role === 'Collaborator') return 'capabilities.readAndWork';
  return 'capabilities.manageProject';
}

export function ProjectMembersPanel({
  projectRole,
  members,
  status,
  reload,
  searchUsers,
  addMember,
  assignRole,
  removeMember,
  transferOwnership,
}: ProjectMembersPanelProps) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [addOpen, setAddOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<ProjectMember | null>(
    null,
  );
  const [pendingId, setPendingId] = useState<string>();
  const [actionError, setActionError] = useState<MemberErrorKind>();

  const transferCandidates = members.filter(member => member.role !== 'Owner');

  const changeRole = async (member: ProjectMember, role: ProjectRole) => {
    setPendingId(member.id);
    setActionError(undefined);
    try {
      await assignRole(member, role);
    } catch (err) {
      setActionError(classifyMemberError(err));
    } finally {
      setPendingId(undefined);
    }
  };

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <Typography variant="body2" className={classes.subtle}>
          {t('members.loading')}
        </Typography>
      );
    }
    if (status === 'error') {
      return (
        <>
          <Typography variant="body2" className={classes.caution}>
            {t('members.loadError')}
          </Typography>
          <Box mt={2}>
            <Button variant="outlined" onClick={() => reload()}>
              {t('members.retry')}
            </Button>
          </Box>
        </>
      );
    }
    if (members.length === 0) {
      return (
        <Typography variant="body2" className={classes.subtle}>
          {t('members.empty')}
        </Typography>
      );
    }
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('table.name')}</TableCell>
            <TableCell>{t('table.email')}</TableCell>
            <TableCell>{t('table.role')}</TableCell>
            <TableCell>{t('table.capabilities')}</TableCell>
            <RoleGate projectRole={projectRole} action="project.invite">
              <TableCell className={classes.actionsCell}>
                {t('table.actions')}
              </TableCell>
            </RoleGate>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map(member => {
            const isOwner = member.role === 'Owner';
            const name = memberDisplayName(member, t('members.selfName'));
            return (
              <TableRow key={member.id}>
                <TableCell>{name}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>
                  <RoleGate
                    projectRole={projectRole}
                    action="project.invite"
                    fallback={
                      <Chip size="small" label={t(`roles.${member.role}`)} />
                    }
                  >
                    {isOwner ? (
                      <Chip size="small" label={t(`roles.${member.role}`)} />
                    ) : (
                      <TextField
                        select
                        size="small"
                        variant="outlined"
                        className={classes.roleSelect}
                        value={member.role}
                        disabled={pendingId === member.id}
                        onChange={event =>
                          changeRole(member, event.target.value as ProjectRole)
                        }
                        SelectProps={{
                          SelectDisplayProps: {
                            'aria-label': t('members.changeRoleFor', { name }),
                          },
                        }}
                      >
                        {ASSIGNABLE_PROJECT_ROLES.map(role => (
                          <MenuItem key={role} value={role}>
                            {t(`roles.${role}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </RoleGate>
                </TableCell>
                <TableCell>{t(capabilityKey(member.role))}</TableCell>
                <RoleGate projectRole={projectRole} action="project.invite">
                  <TableCell className={classes.actionsCell}>
                    {!isOwner && (
                      <Tooltip title={t('members.remove')}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={t('members.removeFor', { name })}
                            disabled={pendingId === member.id}
                            onClick={() => setMemberToRemove(member)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </RoleGate>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <InfoCard
      title={t('details.membersTitle')}
      action={
        <>
          <RoleGate
            projectRole={projectRole}
            action="project.ownership-transfer"
          >
            <Button
              startIcon={<SwapHorizIcon />}
              disabled={transferCandidates.length === 0}
              onClick={() => setTransferOpen(true)}
            >
              {t('members.transferButton')}
            </Button>
          </RoleGate>
          <RoleGate projectRole={projectRole} action="project.invite">
            <Button
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddOpen(true)}
            >
              {t('actions.addMember')}
            </Button>
          </RoleGate>
        </>
      }
    >
      {renderBody()}
      {actionError && (
        <Box mt={2}>
          <Typography variant="body2" className={classes.caution}>
            {t(`members.errors.${actionError}`)}
          </Typography>
        </Box>
      )}
      <AddMemberDialog
        open={addOpen}
        searchUsers={searchUsers}
        onAdd={addMember}
        onClose={() => setAddOpen(false)}
      />
      <TransferOwnershipDialog
        open={transferOpen}
        candidates={transferCandidates}
        onConfirm={member => transferOwnership(member.id)}
        onClose={() => setTransferOpen(false)}
      />
      <RemoveMemberDialog
        member={memberToRemove}
        onConfirm={member => removeMember(member.id)}
        onClose={() => setMemberToRemove(null)}
      />
    </InfoCard>
  );
}
